import { ROUTES } from '@/app/routes';
import MoreSheet from '@/components/navigation/more-sheet';
import { $features } from '@/entities/features-entity';
import { $profile, type PanelRole } from '@/entities/profile-entity';
import { DURATION, EASE, PRESS_TRANSITION, useReducedMotion } from '@/lib/motion-presets';
import { cn } from '@/lib/utils';
import { useUnit } from 'effector-react';
import { motion } from 'motion/react';
import { AtSignIcon, ContainerIcon, LayoutTemplateIcon, LockIcon, MenuIcon, MonitorSmartphoneIcon, SendIcon, UsersIcon } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

type TabId = 'profile' | 'my-clients' | 'clients' | 'workers' | 'apk' | 'sender' | 'landings' | 'more';

interface TabDef {
	id: TabId;
	label: string;
	icon: typeof MonitorSmartphoneIcon;
	path?: string;
	roles?: PanelRole[];
}

const TABS: TabDef[] = [
	{ id: 'profile', label: 'Профиль', icon: AtSignIcon, path: ROUTES.profile },
	{ id: 'my-clients', label: 'Мои', icon: MonitorSmartphoneIcon, path: ROUTES.myClients },
	{ id: 'clients', label: 'Все', icon: LockIcon, path: ROUTES.clients, roles: ['carder', 'admin', 'partner'] },
	{ id: 'workers', label: 'Воркеры', icon: UsersIcon, path: ROUTES.workers, roles: ['partner'] },
	{ id: 'apk', label: 'APK', icon: ContainerIcon, path: ROUTES.apk },
	{ id: 'sender', label: 'Sender', icon: SendIcon, path: ROUTES.sender },
	{ id: 'landings', label: 'Лендинги', icon: LayoutTemplateIcon, path: ROUTES.landings },
	{ id: 'more', label: 'Ещё', icon: MenuIcon },
];

function scrollActiveListToTop() {
	const containers = document.querySelectorAll<HTMLElement>('[data-scroll-container]');
	containers.forEach(el => {
		el.scrollTo({ top: 0, behavior: 'smooth' });
	});
}

function isTabActive(tab: TabDef, pathname: string, moreOpen: boolean): boolean {
	if (tab.id === 'more') return moreOpen;
	if (tab.id === 'profile') {
		return pathname === ROUTES.profile;
	}
	if (tab.id === 'my-clients') {
		return pathname === ROUTES.myClients || pathname.startsWith(ROUTES.myClient);
	}
	if (tab.id === 'clients') {
		return pathname === ROUTES.clients || pathname.startsWith(ROUTES.client);
	}
	if (tab.id === 'workers') {
		return pathname === ROUTES.workers || pathname.startsWith(ROUTES.worker);
	}
	if (tab.id === 'apk') {
		return pathname === ROUTES.apk || pathname.startsWith(`${ROUTES.apk}/`);
	}
	if (tab.id === 'sender') {
		return pathname === ROUTES.sender || pathname.startsWith(`${ROUTES.sender}/`);
	}
	if (tab.id === 'landings') {
		return pathname === ROUTES.landings || pathname.startsWith(`${ROUTES.landings}/`);
	}
	return false;
}

export default function BottomTabBar() {
	const navigate = useNavigate();
	const location = useLocation();
	const profile = useUnit($profile);
	const features = useUnit($features);
	const [moreOpen, setMoreOpen] = useState(false);
	const reducedMotion = useReducedMotion();

	const visibleTabs = TABS.filter(tab => {
		if (tab.id === 'sender') {
			return features.senderEnabled && !features.senderWebappEnabled;
		}
		if (tab.id === 'more') {
			return profile?.role === 'admin';
		}
		if (!tab.roles) return true;
		return profile?.role != null && profile.role !== 'regwait' && tab.roles.includes(profile.role);
	});

	function handleTab(tab: TabDef) {
		if (tab.id === 'more') {
			setMoreOpen(true);
			return;
		}
		if (!tab.path) return;
		if (location.pathname === tab.path) {
			scrollActiveListToTop();
			return;
		}
		navigate(tab.path);
	}

	return (
		<>
			<nav
				className='fixed bottom-0 left-1/2 -translate-x-1/2 z-40 w-full max-w-[460px] border-t border-border bg-background/95 backdrop-blur-sm'
				style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
			>
				<div className='flex items-stretch justify-around h-14'>
					{visibleTabs.map(tab => {
						const Icon = tab.icon;
						const active = isTabActive(tab, location.pathname, moreOpen);
						return (
							<motion.button
								key={tab.id}
								type='button'
								onClick={() => handleTab(tab)}
								whileTap={reducedMotion ? undefined : { scale: 0.9 }}
								transition={PRESS_TRANSITION}
								className={cn(
									'relative flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] transition-colors',
									active ? 'text-foreground' : 'text-muted-foreground',
								)}
							>
								{active && !reducedMotion ? (
									<motion.span
										layoutId='tab-indicator'
										className='absolute bottom-1 left-1/2 h-0.5 w-8 -translate-x-1/2 rounded-full bg-chart-2/60'
										transition={PRESS_TRANSITION}
									/>
								) : active ? (
									<span className='absolute bottom-1 left-1/2 h-0.5 w-8 -translate-x-1/2 rounded-full bg-chart-2/60' />
								) : null}
								<motion.span
									className='flex items-center justify-center'
									animate={{ scale: active ? 1 : 0.92 }}
									transition={{ duration: DURATION, ease: EASE }}
								>
									<Icon size={20} className={cn(active && 'text-chart-2')} />
								</motion.span>
								<span className={cn(active && 'font-semibold')}>{tab.label}</span>
							</motion.button>
						);
					})}
				</div>
			</nav>
			<MoreSheet open={moreOpen} onOpenChange={setMoreOpen} />
		</>
	);
}
